import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminGuard from "@/components/admin/AdminGuard";

export default function LaundryDelegates() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);

  const { data: delegatesData, isLoading: delegatesLoading } = useQuery({
    queryKey: ['delegates'],
    queryFn: () => base44.entities.Delegate.list("-created_date"),
    staleTime: 60000, // 1 minute cache
  });

  const { data: ordersData, isLoading: ordersLoading } = useQuery({
    queryKey: ['laundry-orders'],
    queryFn: () => base44.entities.LaundryOrder.list("-created_date", 500),
    staleTime: 30000, // 30 seconds cache
  });

  const delegates = delegatesData || [];
  const orders = ordersData || [];
  const loading = delegatesLoading || ordersLoading;

  const getDelegateStats = (delegateId) => {
    const delegateOrders = orders.filter(o => o.delegate_id === delegateId && o.status !== 'cancelled');
    const totalOrders = delegateOrders.length;
    const totalAmount = delegateOrders.reduce((sum, o) => sum + (o.total_amount || 0), 0);
    const amountReceived = delegateOrders.reduce((sum, o) => sum + (o.amount_received || 0), 0);
    const cashOrders = delegateOrders.filter(o => o.payment_method === 'cash').length;
    const networkOrders = delegateOrders.filter(o => o.payment_method === 'network').length;
    const transferOrders = delegateOrders.filter(o => o.payment_method === 'transfer').length;
    return { totalOrders, totalAmount, amountReceived, cashOrders, networkOrders, transferOrders };
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return;
    setSaving(true);
    try {
      if (editId) {
        await base44.entities.Delegate.update(editId, form);
      } else {
        await base44.entities.Delegate.create({ ...form, is_active: true, total_orders: 0, current_orders: 0 });
      }
      setForm({ name: "", phone: "", notes: "" });
      setEditId(null);
      setShowForm(false);
      queryClient.invalidateQueries({ queryKey: ['delegates'] });
    } catch (err) {
      console.error('Error saving delegate:', err);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (d) => {
    try {
      await base44.entities.Delegate.update(d.id, { is_active: !d.is_active });
      queryClient.invalidateQueries({ queryKey: ['delegates'] });
    } catch (err) {
      console.error('Error toggling delegate:', err);
    }
  };

  const startEdit = (d) => {
    setForm({ name: d.name, phone: d.phone, notes: d.notes || "" });
    setEditId(d.id);
    setShowForm(true);
  };

  return (
    <AdminGuard>
      <AdminLayout>
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-black text-foreground">👤 المندوبون</h1>
              <p className="text-muted-foreground text-sm">{delegates.length} مندوب</p>
            </div>
            <button
              onClick={() => { setShowForm(!showForm); setEditId(null); setForm({ name: "", phone: "", notes: "" }); }}
              className="px-4 py-2 bg-cyan-600 text-white rounded-xl text-sm font-bold hover:bg-cyan-700 transition-all"
            >
              + إضافة مندوب
            </button>
          </div>

          {showForm && (
            <form onSubmit={handleSave} className="bg-card rounded-2xl p-5 border border-border shadow-sm space-y-3">
              <h3 className="font-bold text-foreground">{editId ? "تعديل مندوب" : "مندوب جديد"}</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-muted-foreground block mb-1">الاسم *</label>
                  <input
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full bg-background border border-border rounded-xl px-3 py-2 text-sm outline-none focus:border-cyan-500"
                    placeholder="اسم المندوب"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-muted-foreground block mb-1">الجوال *</label>
                  <input
                    value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    className="w-full bg-background border border-border rounded-xl px-3 py-2 text-sm outline-none focus:border-cyan-500"
                    placeholder="05xxxxxxxx"
                    dir="ltr"
                  />
                </div>
              </div>
              <input
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                className="w-full bg-background border border-border rounded-xl px-3 py-2 text-sm outline-none focus:border-cyan-500"
                placeholder="ملاحظات (اختياري)"
              />
              <div className="flex gap-3">
                <button type="submit" disabled={saving} className="flex-1 bg-cyan-600 text-white font-bold py-2.5 rounded-xl hover:bg-cyan-700 transition-all disabled:opacity-60 text-sm">
                  {saving ? "⏳ جاري الحفظ..." : "💾 حفظ"}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2.5 bg-secondary rounded-xl text-sm font-bold">
                  إلغاء
                </button>
              </div>
            </form>
          )}

          {loading ? (
            <div className="space-y-3">
              {[1,2,3].map(i => <div key={i} className="h-20 bg-card rounded-2xl animate-pulse" />)}
            </div>
          ) : delegates.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <div className="text-4xl mb-2">👤</div>
              <p>لا يوجد مندوبون بعد</p>
            </div>
          ) : (
            <div className="space-y-3">
              {delegates.map(d => {
                const stats = getDelegateStats(d.id);
                return (
                  <div key={d.id} className="bg-card rounded-2xl p-4 border border-border">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg ${d.is_active ? "bg-green-100 dark:bg-green-900/30" : "bg-gray-100 dark:bg-gray-800"}`}>
                          {d.is_active ? "✅" : "⛔"}
                        </div>
                        <div>
                          <p className="font-bold text-foreground">{d.name}</p>
                          <p className="text-sm text-muted-foreground" dir="ltr">{d.phone}</p>
                          {d.notes && <p className="text-xs text-muted-foreground">{d.notes}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => startEdit(d)} className="text-xs px-3 py-1.5 bg-secondary rounded-lg hover:bg-secondary/80 font-medium">
                          ✏️ تعديل
                        </button>
                        <button onClick={() => toggleActive(d)} className={`text-xs px-3 py-1.5 rounded-lg font-medium ${d.is_active ? "bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400" : "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"}`}>
                          {d.is_active ? "تعطيل" : "تفعيل"}
                        </button>
                      </div>
                    </div>
                    
                    {/* الإحصائيات */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-border">
                      <div className="bg-blue-50 dark:bg-blue-950/20 rounded-xl p-3 text-center">
                        <p className="text-xs text-muted-foreground mb-1">إجمالي الطلبات</p>
                        <p className="text-lg font-black text-blue-600 dark:text-blue-400">{stats.totalOrders}</p>
                      </div>
                      <div className="bg-green-50 dark:bg-green-950/20 rounded-xl p-3 text-center">
                        <p className="text-xs text-muted-foreground mb-1">المبلغ المستلم</p>
                        <p className="text-lg font-black text-green-600 dark:text-green-400">{stats.amountReceived.toLocaleString()} ﷼</p>
                      </div>
                      <div className="bg-purple-50 dark:bg-purple-950/20 rounded-xl p-3 text-center">
                        <p className="text-xs text-muted-foreground mb-1">إجمالي الفواتير</p>
                        <p className="text-lg font-black text-purple-600 dark:text-purple-400">{stats.totalAmount.toLocaleString()} ﷼</p>
                      </div>
                      <div className="bg-orange-50 dark:bg-orange-950/20 rounded-xl p-3 text-center">
                        <p className="text-xs text-muted-foreground mb-1">المتبقي</p>
                        <p className="text-lg font-black text-orange-600 dark:text-orange-400">{(stats.totalAmount - stats.amountReceived).toLocaleString()} ﷼</p>
                      </div>
                    </div>
                    
                    {/* طرق الدفع */}
                    <div className="flex gap-2 mt-3 pt-3 border-t border-border">
                      <span className="text-xs px-2 py-1 bg-cash/10 text-cash rounded-md font-medium">💵 نقدي: {stats.cashOrders}</span>
                      <span className="text-xs px-2 py-1 bg-network/10 text-network rounded-md font-medium">🌐 شبكة: {stats.networkOrders}</span>
                      <span className="text-xs px-2 py-1 bg-transfer/10 text-transfer rounded-md font-medium">🔄 تحويل: {stats.transferOrders}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </AdminLayout>
    </AdminGuard>
  );
}